import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaEnvelope, FaLock, FaSignInAlt } from 'react-icons/fa';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Disable any potential axios response interceptors that might cause redirects
    useEffect(() => {
        console.log("Setting up axios response interceptor");

        // Remove existing interceptors if any
        const axiosInterceptors = axios.interceptors.response;
        const existingInterceptors = axiosInterceptors.handlers.length;
        console.log(`Found ${existingInterceptors} existing axios response interceptors`);

        // Add our own interceptor to prevent redirects for 401/403 responses
        const interceptorId = axios.interceptors.response.use(
            response => response,
            error => {
                console.log("Axios interceptor caught error:", {
                    status: error.response?.status,
                    url: error.config?.url
                });

                // Only prevent default redirect behavior for login endpoint
                if (error.config?.url === '/login') {
                    console.log("Login endpoint error intercepted - preventing default behavior");
                    return Promise.reject(error);
                }

                return Promise.reject(error);
            }
        );

        return () => {
            console.log("Removing axios interceptor");
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    useEffect(() => {
        document.body.style.overflow = 'initial !important';

        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Prevent error message from being cleared on rerenders
    useEffect(() => {
        // Only clean up error on unmount
        return () => {
            setError('');
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation(); // Stop event propagation

        console.log("Login form submitted");
        setIsLoading(true);
        setError('');

        try {
            console.log("Attempting to log in with:", { email, passwordLength: password.length });

            const response = await axios.post('/login', {
                email,
                password
            });

            console.log("Login response received:", {
                status: response.status,
                hasToken: !!response.data.token,
                data: response.data
            });

            if (!response.data || !response.data.token) {
                console.log("No token in response - treating as error");
                throw new Error("Keine gültige Antwort vom Server erhalten");
            }

            localStorage.setItem('token', response.data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            console.log("Token stored in localStorage and axios headers set");

            setIsLoading(false);
            console.log("Navigating to home page");
            navigate('/');
        } catch (err) {
            console.error("Login error:", {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                message: err.message
            });

            setIsLoading(false);
            setPassword(''); // Clear password on error

            // Check if the error is due to email verification
            if (err.response?.status === 403 && err.response?.data?.verification_required) {
                console.log("Email verification required error");
                setError(
                    <div>
                        {err.response.data.message || 'Sie müssen Ihre E-Mail-Adresse bestätigen, bevor Sie sich anmelden können.'} <br/>
                        <Link to={`/resend-verification`} className="underline text-blue-700">
                            Bestätigungs-E-Mail erneut senden
                        </Link>
                    </div>
                );
            }
            // Check for authentication failures (incorrect credentials)
            else if (err.response?.status === 401 ||
                    (err.response?.status === 422 && err.response?.data?.errors?.email)) {
                console.log("Invalid credentials error");
                setError('Die eingegebene E-Mail-Adresse oder das Passwort ist nicht korrekt. Bitte überprüfen Sie Ihre Eingaben.');
            }
            else {
                console.log("General login failure");
                setError(err.response?.data?.message || 'Anmeldung fehlgeschlagen');
            }

            // Ensure the error state persists
            setTimeout(() => {
                console.log("Error state after timeout:", { error: error ? 'Error set' : 'No error' });
            }, 500);

            // Explicitly prevent default form behavior
            return false;
        }
    };

    // Add a logger effect to track state changes
    useEffect(() => {
        console.log("Login component state updated:", {
            email: email ? 'Set' : 'Empty',
            password: password ? 'Set' : 'Empty',
            error: error ? 'Error present' : 'No error',
            isLoading
        });
    }, [email, password, error, isLoading]);

    // Add a cleanup logger
    useEffect(() => {
        console.log("Login component mounted");

        // Check if there's an auth token already
        const token = localStorage.getItem('token');
        console.log("Initial token check:", { hasToken: !!token });

        return () => {
            console.log("Login component unmounting");
        };
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <motion.div
                className="max-w-md w-full space-y-6 bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="flex flex-col items-start justify-start">
                    <img
                        src="/images/logo/logo-bernie.svg"
                        alt="Bernie Logo"
                        className="h-8 mb-2"
                    />
                </div>

                {error && (
                    <motion.div
                        className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    </motion.div>
                )}

                <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">E-Mail-Adresse</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaEnvelope className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out sm:text-sm"
                                    placeholder="name@beispiel.de"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaLock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out sm:text-sm"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                                    Angemeldet bleiben
                                </label>
                            </div>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                            <div className="text-sm">
                                <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                                    Passwort vergessen?
                                </Link>
                            </div>

                            <div className="text-sm">
                                <a href="mailto:office@quantonomics.com" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:text-blue-500">
                                    Kontakt
                                </a>
                            </div>
                        </div>
                    </div>

                    <div>
                        <motion.button
                            type="submit"
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-150"
                            disabled={isLoading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {isLoading ? (
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <>
                                    <FaSignInAlt className="mr-2" />
                                    Anmelden
                                </>
                            )}
                        </motion.button>
                    </div>

                    <div className="text-sm text-center mt-6 pt-4 border-t border-gray-200">
                        <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                            Noch kein Konto? <span className="underline">Jetzt registrieren</span>
                        </Link>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default Login;
